import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatINR } from "@/lib/api";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import WishlistButton from "@/components/WishlistButton";
import { toast } from "sonner";

const swatch = (c) => String(c || "").trim().toLowerCase().replace(/\s+/g, "");

export default function ProductCard({ product, onQuickAdd }) {
  const [hover, setHover] = React.useState(false);
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const img1 = product.images?.[0];
  const img2 = product.images?.[1] || img1;

  const quickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast("Sign in to add to cart"); navigate("/auth?redirect=" + encodeURIComponent(window.location.pathname)); return; }
    try {
      await cart.add(product.id, { quantity: 1, size: product.sizes?.[0], color: product.colors?.[0] });
      toast.success(`Added ${product.name}`);
    } catch (er) { toast.error("Could not add"); }
    onQuickAdd?.(product);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group block"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
        <img src={img1} alt={product.name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${hover ? "opacity-0" : "opacity-100"}`} />
        <img src={img2} alt={`${product.name} hover`} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${hover ? "opacity-100" : "opacity-0"}`} />
        <WishlistButton id={product.id} size={18} className="absolute top-3 right-3 z-10" />
        <button
          data-testid={`quick-add-${product.id}`}
          onClick={quickAdd}
          className="absolute bottom-3 right-3 w-10 h-10 bg-white text-black hover:bg-squire-jeans hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
          aria-label="Quick add"
        >
          <ShoppingBag size={16} />
        </button>
      </div>
      <div className="mt-4 flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#4B5563]">{product.sub_category || product.category}</div>
          <div className="font-display text-lg mt-1 leading-tight">{product.name}</div>
        </div>
        <div className="font-semibold whitespace-nowrap">{formatINR(product.price)}</div>
      </div>
      {product.colors?.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2" data-testid={`product-card-colors-${product.id}`}>
          {product.colors.slice(0, 5).map((c) => (
            <span key={c} title={c} className="w-3.5 h-3.5 rounded-full border border-black/20" style={{ background: swatch(c) }} />
          ))}
        </div>
      )}
    </Link>
  );
}
